import type { Theme } from '@mui/material'
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ApplicationTypes } from 'element-domain'
import { useCallback, useMemo } from 'react'

import { useLogger } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { ShopifyCartError, addVariantToCart } from '../../../../shopify/shopify-ajax-client'
import { commitPopOrNavigateToSummary } from '../../../../systems/commit-pop-or-summary'
import { configuratorInterface } from '../../../../systems/configurator-interface'

interface FinalizeCartSuccessDialogContentProps {
  finalizationUuid: string
  onClose?: () => void
}

type AddToCartFailureStage =
  | 'finalization_polling'
  | 'shopify_product_creation'
  | 'shopify_variant_resolution'
  | 'shopify_cart_request'

export const FinalizeCartSuccessDialogContent = ({
  finalizationUuid,
  onClose,
}: FinalizeCartSuccessDialogContentProps) => {
  const { navigationSystem, storeSystem } = configuratorInterface.useDomain()

  const { configurationId } = configuratorInterface.useDomain()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const logger = useLogger()

  const cartUrl = useMemo(() => {
    if (!window?.Shopify) {
      logger.warn('Shopify is not loaded')
      return null
    }
    return window.Shopify.routes.root + 'cart'
  }, [logger])

  const handleBack = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      commitPopOrNavigateToSummary(navigationSystem, storeSystem, ApplicationTypes.NavigationState.Summary)
    }
  }, [onClose, navigationSystem, storeSystem])

  const getResult = useCallback(async () => {
    let failureStage: AddToCartFailureStage = 'finalization_polling'
    let shopifyProduct:
      | Awaited<ReturnType<typeof apiClient.waitForFinalizationToComplete>>['shopifyProduct']
      | undefined

    try {
      const data = await apiClient.waitForFinalizationToComplete(configurationId, finalizationUuid, {
        pollingTimeout: 20 * 60 * 1000,
      })
      shopifyProduct = data.shopifyProduct

      failureStage = shopifyProduct?.success === false ? 'shopify_product_creation' : 'shopify_variant_resolution'
      if (!shopifyProduct?.productVariantId) {
        logger.error('PRODUCT VARIANT ID NOT FOUND', { data })
        throw new Error(shopifyProduct?.error ?? 'PRODUCT VARIANT ID NOT FOUND')
      }

      failureStage = 'shopify_cart_request'
      return await addVariantToCart(shopifyProduct.productVariantId.toString())
    } catch (error) {
      const productCreated =
        shopifyProduct == null
          ? 'unknown'
          : shopifyProduct.success === true || shopifyProduct.productId != null
            ? true
            : shopifyProduct.success === false
              ? false
              : 'unknown'
      const errorObject = error instanceof Error ? error : new Error('Unknown error')

      window.dataLayer = window.dataLayer ?? []
      window.dataLayer.push({
        event: 'configurator_add_to_cart_failed',
        configurationId,
        finalizationUuid,
        failureStage,
        productCreated,
        productId: shopifyProduct?.productId?.toString(),
        productVariantId: shopifyProduct?.productVariantId?.toString(),
        productTitle: shopifyProduct?.productTitle,
        productUrl: shopifyProduct?.productUrl,
        productSku: shopifyProduct?.sku,
        productPrice: shopifyProduct?.price,
        productStatusMessage: shopifyProduct?.message,
        productCreationError: shopifyProduct?.error,
        errorName: errorObject.name,
        errorMessage: errorObject.message,
        ...(error instanceof ShopifyCartError
          ? {
              httpStatus: error.status,
              httpStatusText: error.statusText,
              shopifyError: error.shopifyMessage,
            }
          : {}),
        quantity: 1,
        result: 'failure',
        source: 'salsita-configurator',
        timestamp: new Date().toISOString(),
      })

      logger.error('Adding finalized configuration to cart failed', { failureStage, productCreated }, errorObject)
      throw errorObject
    }
  }, [apiClient, configurationId, finalizationUuid, logger])

  const { data, isError } = useQuery({
    queryKey: ['add-finalized-configuration-to-cart', finalizationUuid],
    queryFn: getResult,
    // This query performs a cart mutation. It must never be repeated by React Query's
    // normal focus, reconnect, mount, stale-data, or retry behavior.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const handleViewCart = useCallback(() => {
    if (cartUrl) {
      window.open(cartUrl, '_self')
    }
  }, [cartUrl])

  return (
    <Dialog.ContentLayout
      controlsPosition="end"
      header={
        <Box
          component="div"
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.background.default,
            blockSize: 180,
            color: theme.palette.success.main,
            display: 'grid',
            placeItems: 'center',
          })}
        >
          <PhxIcon name="CheckCircle" style={{ fontSize: 80 }} />
        </Box>
      }
      body={
        <Stack sx={{ gap: 2, px: 1 }}>
          {isError && (
            <Alert severity="error">
              <b>Couldn't add to cart.</b>
              <br />
              Please try again later or contact support if the issue persists.
            </Alert>
          )}
          {!data && (
            <>
              <Typography>Creating new Cart Item...</Typography>
              <LinearProgress />
            </>
          )}
          {data && (
            <>
              <Typography variant="h6">Added to Cart!</Typography>
              <Typography variant="body1">
                Your custom table configuration has been successfully added to your cart.
              </Typography>
            </>
          )}
        </Stack>
      }
      controls={
        <>
          <Button variant="text" onClick={handleBack} startIcon={<PhxIcon name="ArrowLeft" />}>
            Back to Summary
          </Button>
          {data && (
            <Button variant="contained" onClick={handleViewCart}>
              View Cart
            </Button>
          )}
        </>
      }
    />
  )
}
